import { Injectable } from '@angular/core';
import { RequestCommon, ResponseCommon } from 'sg-api';
import { HttpBusinessErrorResponse } from '../http-business-error-response';
import { TimeoutError } from 'rxjs';
import { ToastMessageService } from '../services/toast-message.service';

import { HttpClient } from '@angular/common/http';
import { BusinessStagnationResponseService } from '../business-stagnation-response.service';
import { BusinessStagnationResponse } from '../business-stagnation-response';

type wkRequestCommon = {
  requestCommon?: RequestCommon;
}

/**
 * Toastでメッセージを表示する
 * @export
 * @class ToastMessageService
 */
@Injectable({
  providedIn: 'root'
})
export class ApiCommonService {

  messageTemplate = null;

  constructor(
    private toastMessageService: ToastMessageService,
    private http: HttpClient,
    private businessStagnationResponseService: BusinessStagnationResponseService
  ) {
    this.initMessageTemplate();
  }

  /**
   * メッセージカスタマイズ定義ファイルを取得し内部で保持する
   *
   * @param {*} err
   * @memberof ApiCommonService
   */
  private initMessageTemplate() {
    this.http.get('assets/errMsg/messageTemplate.json').subscribe(
      data => {
        this.messageTemplate = data;
      },
      error => {
        console.log('メッセージカスタマイズ定義ファイルが存在しません:' + error);
      }
    );
  }

  /**
   * Errorメッセージが設定されているかチェックし,業務エラーの場合は例外をスローする.
   * ※引数には、ResponseCommonが存在する必要がある.
   *
   * @param {*} res
   * @return {*}  {void}
   * @throws HttpBusinessErrorResponse
   * @memberof ApiCommonService
   */
  public checkErrorNoSearchData(res: any): void {
    var resp = null;
    if (res.body.responseCommon != null) {
      resp = res.body.responseCommon as ResponseCommon;
    }

    if (resp.resultCode == '0' || resp.resultCode == '1' || (resp.resultCode == '9' && resp.resultDetailCode == "SZZZ_E002")) {
      // Error無しのため、処理を終了する。
      return;
    }

    if (resp.resultCode == '9' && resp.resultDetailCode == "SZZZ_E008") {
      // 業務閉塞中画面表示用の通知を発行
      this.businessStagnationResponseService.notifyError();
      // 後続処理を停止させるため、例外をスロー
      throw new BusinessStagnationResponse(res.body);
    }

    // 業務エラーのため、例外をスローする。
    throw new HttpBusinessErrorResponse(res.body, this.getErrorMessage(resp.errors));
  }

  /**
   * Errorメッセージが設定されているかチェックし,業務エラーの場合は例外をスローする.
   * ※引数には、ResponseCommonが存在する必要がある.
   *
   * @param {*} res
   * @return {*}  {void}
   * @throws HttpBusinessErrorResponse
   * @memberof ApiCommonService
   */
  public checkError(res: any): void {
    var resp = null;
    if (res.body.responseCommon != null) {
      resp = res.body.responseCommon as ResponseCommon;
    }

    if (resp.resultCode == '0' || resp.resultCode == '1') {
      // Error無しのため、処理を終了する。
      return;
    }

    if (resp.resultCode == '9' && resp.resultDetailCode == "SZZZ_E008") {
      // 業務閉塞中画面表示用の通知を発行
      this.businessStagnationResponseService.notifyError();
      // 後続処理を停止させるため、例外をスロー
      throw new BusinessStagnationResponse(res.body);
    }

    // 業務エラーのため、例外をスローする。
    throw new HttpBusinessErrorResponse(res.body, this.getErrorMessage(resp.errors));
  }

  /**
   * catchErrorで検出されたエラー情報を画面に表示する
   *
   * @param {*} err
   * @memberof ApiCommonService
   */
  public dispErrorMessage(err: any, id?: string, apiName?: string): void {
    // API名の設定
    const outputApi = (apiName != null && apiName != '') ? apiName + ' ' : '';
    if (err instanceof TimeoutError) {
      // Timeoutの場合
      this.toastMessageService.addError(outputApi + 'リクエストがタイムアウトしました');
      return;
    }

    if (err.error && err.error.responseCommon) {
      var resp = err.error.responseCommon as ResponseCommon;
      if (resp.errors) {
        if ((id != null && id != '') && (apiName != null && apiName != '')) {
          this.http.get('assets/errMsg/' + id + '/' + apiName + '.json').subscribe(
            data => {
              resp.errors.forEach(error => {
                var dispMessage = this.getErrorMessageCustomize(error);
                dispMessage = this.convertMessage(id, apiName, dispMessage, data);
                this.toastMessageService.addError(outputApi + dispMessage);
              });
            },
            error => {
              console.log('エラーメッセージ変換定義ファイルが存在しません:' + id + ', ' + apiName + ':' + error);
              resp.errors.forEach(error => {
                var dispMessage = this.getErrorMessageCustomize(error);
                this.toastMessageService.addError(outputApi + dispMessage);
              });
            }
          );
        }
        else {
          resp.errors.forEach(error => {
            var dispMessage = this.getErrorMessageCustomize(error);
            this.toastMessageService.addError(outputApi + dispMessage);
          });
        }
      }
      else {
        // errorsに情報未設定
        this.toastMessageService.addError(outputApi + 'サーバからエラーが返却されました（エラー情報未設定）');
      }
    }
    else {
      this.toastMessageService.addError(outputApi + '予期しないエラーが発生しました');
    }
  }

  /**
   * RequestCommonの情報を作成する
   *
   * @return {*}  {RequestCommon}
   * @memberof ApiCommonService
   */
  public setRequestCommon<T extends wkRequestCommon>(obj: T): T {
    obj.requestCommon = {
      // businessDateはrest側で日付をだますときに使う項目なので設定不要
      startLine: 0,
      getNumber: 0,
      dryRun: false,
      requestCustomize: obj.requestCommon?.requestCustomize || {},
    };

    return obj;
  }

  /**
   * オブジェクト内の特定の型の要素を指定した方法で変換する
   *
   * @param {*} obj 対象オブジェクト
   * @param {string} targetType 変換対象の型
   * @param {*} cnvMethod 変換関数
   * @memberof ApiCommonService
   */
  public convertElementInObject(obj: any, targetType: string, cnvMethod: any): void {
    // オブジェクト内に要素が存在するか判定
    if ((obj != null) && (Object.keys(obj).length != 0)) {
      for (let item of Object.keys(obj)) {
        // 対象要素の型が変換対象の型と一致するか判定
        if (toString.call(obj[item]) === targetType) {
          // 変換関数で変換した値に変更
          console.log('convert type:' + item);
          obj[item] = cnvMethod(obj[item]);
          continue;
        }
        // typeof null は、objectと判断されるため、次のチェックに進む
        if (obj[item] == null) continue;

        if (typeof obj[item] === 'object') {
          // 再起呼び出し
          this.convertElementInObject(obj[item], targetType, cnvMethod);
        }
      }
    }
  }

  /**
   * Objectの初期化判定を行う。（true：初期化が必要。false：初期化不要）
   *
   * @return {*}  {boolean}
   * @memberof checkInitialObject
   */
  public checkInitialObject<T>(obj: T): boolean {
    return (!obj || (Object.keys(obj).length == 0));
  }

  /**
   * keyを日本語名に変換する
   * @param id 業務id（sys, ff, prov, eec, bill）
   * @param apiName API名（例：SZOA103A）
   * @param errorMessage responseCommon.errors[].message
   * @returns keyを日本語変換したメッセージ
   * @memberof checkInitialObject
   */
  private convertMessage(id: string, apiName: string, errorMessage: string, data: object): string {

    if (errorMessage == null) {
      console.log('エラーメッセージが設定されていません:' + id + ', ' + apiName);
      return errorMessage;
    }

    let matched = false;
    if (data != null) {
      for (let key in data) {
        if (this.checkCharacter(errorMessage, key)) {
          // 一致する部分が存在する場合
          if (data[key] != '') {
            // 変換日本語名が空文字でない場合は、日本語名に置換
            errorMessage = errorMessage.replace(key, data[key]);
          }
          matched = true;
        }
      }

      // 一致しなかった場合、末尾のkey名でマッチング
      if (!matched) {
        for (let key in data) {
          const splitKeyArray = key.split('.');
          const word = splitKeyArray[splitKeyArray.length - 1];
          if (this.checkCharacter(errorMessage, word)) {
            // 一致する部分が存在する場合
            if (data[key] != '') {
              // 変換日本語名が空文字でない場合は、日本語名に置換
              errorMessage = errorMessage.replace(word, data[key]);
            }
          }
        }
      }
    }

    return errorMessage;
  }

  /**
   * 対象メッセージ内にキーが単語として存在するかチェック
   * @param word メッセージ
   * @param key キー
   * @returns
   */
  private checkCharacter(word: string, key: string): boolean {
    // メッセージ内のキーの位置を取得
    const index = word.indexOf(key);
    if (index <= -1) {
      // 存在しない
      return false;
    }

    // メッセージからキーの次の文字を取得
    const data = word.slice(index + key.length, index + key.length + 1);
    // キーが文字か判定
    if (data.match(/^[0-9a-zA-Z]$/) != null) {
      // 文字の場合
      return false;
    }

    return true;
  }


  /**
   * エラーメッセージを作成
   * @param errors
   * @returns
   */
  private getErrorMessage(errors: any): string {
    var dispMessage = "";
    for (var i = 0; i < errors.length; i++) {
      dispMessage += errors[i].errorCode + ':' + errors[i].message;
      // 改行を追加する前に、現在のエラーが最後のエラーでないかどうか
      if (i < errors.length - 1) {
        dispMessage += '\r\n';
      }
    }
    return dispMessage;
  }

  /**
   * エラーメッセージカスタマイズを作成
   * @param error
   * @returns
   */
  private getErrorMessageCustomize(error: any): string {
    var dispMessage = "";

    //エラーコードがメッセージカスタマイズに存在するか確認する。
    if (this.messageTemplate && this.messageTemplate[error.errorCode]) {
      dispMessage = dispMessage + this.formatMessage(this.messageTemplate[error.errorCode], error.itemNames);
    }
    else {
      dispMessage = dispMessage + error.errorCode + ':' + error.message;
    }

    return dispMessage;
  }

  /**
   * メッセージフォーマット機能
   * @param msg
   * @param args
   * @returns
   */
  private formatMessage(msg: string, args: any[]): string {
    if (args != null) {
      return msg.replace(/\{(\d+)\}/g, (m, k) => {
        return args[k];
      });
    }
    else {
      return msg;
    }
  }

}